import { randomId } from '../utils/randomId';
import { GameState, Player, ScoreEntry, GameEvent } from '../types/game';
import { validateAndComputeScore } from '../domain/scoreComputation';
import { PLAYER_NAME_MIN_LEN, PLAYER_NAME_MAX_LEN, PLAYER_NAME_REGEX, SCORE_DELTA_MIN, SCORE_DELTA_MAX, HISTORY_LIMIT } from '../utils/constants';
import { gameRules } from '../domain/gameRules';
import type { TrackZone } from '../domain/zones';
// Explicit .ts extension to satisfy bundler moduleResolution that seemed to miss the new file in incremental build.
import { sanitizePlayerName } from '../utils/sanitize.ts';
import { applyTurnStats } from '../domain/stats';
import { computeTurnOutcome } from '../domain/turnOutcome';
import { generateGameId } from '../utils/gameId';
import { debugLog as baseDebugLog } from './debug/debugLog';
import { isVerboseLoggingEnabled, logger } from '../utils/logger';
import { OnlineSnapshot } from '../types/snapshots';
import { OnlineLobbyStateSnapshot } from '../types/transport';
import { assignPlayerColors, pickNextPlayerColor } from '../utils/playerColors';
import {
  deriveFirstRollInfoFromEvents,
  ensureOnlineTurnCycle,
  pushNotice,
  updatePlayerPositions,
  withDefaultStatus,
} from './reducerHelpers';
import { computeRollState, computeToggleKeepResult, handleApplyDiceKeep, handleEndTurnInternal, handleMarkTurnFinalizing } from './handlers/turn/turnIndex';
import {
  handleBeginOnlineGame,
  handleFinalizeOrder,
  handleInitOnlineLobby,
  handleMergeExternalPlayers,
  handleSetLobbyPhase,
  handleToggleReady,
} from './lobbyHandlers';
import {
  handleApplyEvent,
  handleHydrateLobbyState,
  handleHydrateFromSnapshot,
  handleTrimEvents,
} from './syncHandlers';
import { handleUndoLastAction, handleUndoTurn } from './undoHandlers';

// Helper debug (kept local so reducer file can be used in isolation in tests)
declare global { interface Window { __DICE_DEBUG__?: boolean } }
const debug = (label: string, payload: unknown) => baseDebugLog(label, payload, { source: 'gameReducer' });

export type GameAction =
  | { type: 'ADD_PLAYER'; name: string; id?: string }
  | { type: 'MARK_SELF_PLAYER'; playerId: string | null }
  | { type: 'MERGE_EXTERNAL_PLAYERS'; players: Player[] }
  | { type: 'REORDER_PLAYERS'; players: Player[] }
  | { type: 'START_GAME'; gameId?: string; mode?: GameState['mode']; groupToken?: string | null; groupId?: string | null; zones?: TrackZone[] }
  | { type: 'ADD_SCORE'; playerId: string; points: number; turn?: number }
  | { type: 'UNDO_LAST_ACTION' }
  | { type: 'RESET_GAME'; mode?: GameState['mode'] }
  | { type: 'TOGGLE_TUNNELS' }
  | { type: 'TOGGLE_RESCUE_BY_FIVE' }
  | { type: 'TOGGLE_ZONES' }
  | { type: 'TOGGLE_ZONES_AVOID_TUNNELS' }
  | { type: 'UPDATE_SETTINGS'; settings: GameState['settings'] }
  | { type: 'LOAD_GAME'; gameState: GameState }
  | { type: 'SET_GAME_ID'; id: string }
  | { type: 'SET_MODE'; mode: GameState['mode'] }
  | { type: 'SET_HISTORY'; history: ScoreEntry[] }
  | { type: 'SET_EVENTS'; events: GameEvent[] }
  | { type: 'ADD_EVENT'; event: GameEvent }
  | { type: 'TRIM_EVENTS'; events: GameEvent[] }
  | { type: 'ROLLBACK_EVENT'; eventId: string }
  | { type: 'TOGGLE_READY'; playerId: string; ready: boolean }
  | { type: 'INIT_ONLINE_LOBBY'; gameId?: string; groupToken?: string | null; groupId?: string | null }
  | { type: 'FINALIZE_ORDER'; order: string[] }
  | { type: 'SET_LOBBY_PHASE'; phase: 'LOBBY' | 'ORDER_REVEAL' | 'IN_GAME' }
  | { type: 'GAME_STARTED'; firstPlayerId: string }
  | { type: 'APPLY_DICE_ROLL'; playerId: string; rollIndex: number; dice: number[]; keptMask: boolean[]; eventId?: string }
  | { type: 'APPLY_DICE_KEEP'; playerId: string; rollIndex: number; keptMask: boolean[] }
  | { type: 'ROLL_DICE'; playerId: string; rollIndex: number; dice: number[]; keptMask: boolean[]; eventId: string; isHotDiceReset?: boolean; rolledDice?: number[]; rolledIndices?: number[]; rollMeta?: { points: number; scoringDiceCount: number; sum: number; allScoring: boolean; rescueAvailable: boolean; bust: boolean; didBust: boolean } }
  | { type: 'TOGGLE_KEEP'; index: number }
  | { type: 'END_TURN_INTERNAL'; playerId: string }
  | { type: 'UNDO_TURN'; turn: number }
  | { type: 'MARK_TURN_FINALIZING' }
  | { type: 'APPLY_EVENT'; event: GameEvent }
  | { type: 'HYDRATE_FROM_SNAPSHOT'; snapshot: OnlineSnapshot }
  | { type: 'HYDRATE_LOBBY_STATE'; snapshot: OnlineLobbyStateSnapshot }
  | { type: 'ADD_NOTICE'; notice: import('../types/game').GameNotice }
  | { type: 'CLEAR_NOTICE'; id?: string; playerId?: string }
  | { type: 'SET_GROUP'; groupId?: string | null; groupToken?: string | null };

export const initialState: GameState = {
  id: '',
  players: [],
  spectators: [],
  currentPlayerIndex: 0,
  turnNumber: 1,
  isGameOver: false,
  history: [],
  events: [],
  notices: [],
  firstEntries: {},
  mode: 'REAL',
  groupId: null,
  groupToken: null,
  settings: { tunnels: true, rescueByFive: true, zones: false, zonesAvoidTunnels: false },
  lobby: undefined,
  turnCycle: undefined,
  zones: undefined,
  rapidTurnMeta: null,
  rapidPenaltyPresentation: null,
  postGameSummary: null,
  segmentsOwnerId: undefined,
};

function baseStateForReplay(state: GameState): GameState {
  return {
    ...initialState,
    id: state.id,
    mode: state.mode,
    groupId: state.groupId ?? null,
    groupToken: state.groupToken ?? null,
    lobby: state.mode === 'ONLINE' ? { phase: 'LOBBY', readyMap: {}, nextGameParticipation: {} } : undefined,
  };
}

export function gameReducer(state: GameState, action: GameAction): GameState { // export dla testów
  // Normalizuj kolory graczy (starsze snapshoty mogą ich nie mieć)
  if (state.players.some((p) => !p.color)) {
    state = { ...state, players: assignPlayerColors(state.players) };
  }
  if (state.players.some((p) => !p.status || !p.role)) {
    state = { ...state, players: state.players.map(withDefaultStatus) };
  }
  state = ensureOnlineTurnCycle(state);
  let newState: GameState;
  switch (action.type) {
    case 'ADD_NOTICE': {
      const notice = action.notice;
      newState = pushNotice(state, notice);
      break;
    }
    case 'CLEAR_NOTICE': {
      const { id, playerId } = action;
      const filtered = (state.notices || []).filter(n => {
        if (id) return n.id !== id;
        if (playerId) return n.playerId !== playerId;
        return false;
      });
      newState = { ...state, notices: filtered };
      break;
    }
    case 'ADD_PLAYER': {
      // Guard: w trybie ONLINE gracze muszą być dodawani WYŁĄCZNIE przez zdarzenia (APPLY_EVENT)
      if (state.mode === 'ONLINE') {
        if (isVerboseLoggingEnabled()) {
          logger.warn('GAME_REDUCER_ADD_PLAYER_IGNORED_ONLINE');
        }
        return state;
      }
      const original = action.name || '';
      const raw = sanitizePlayerName(original);
      const exists = state.players.some(p => p.name.toLowerCase() === raw.toLowerCase());
      // Jeśli sanitizacja usunęła niedozwolone znaki (różnica znakowa) – traktuj jako invalid (hardening)
      const sanitizedDiffers = original.normalize('NFKC') !== raw;
      const valid = !sanitizedDiffers && raw.length >= PLAYER_NAME_MIN_LEN && raw.length <= PLAYER_NAME_MAX_LEN && PLAYER_NAME_REGEX.test(raw) && !exists;
      if (!valid) return state;
      const playerId = action.id || randomId();
      const usedColors = new Set(state.players.map((p) => p.color).filter(Boolean) as string[]);
      const newPlayer: Player = {
        id: playerId,
        name: raw,
        score: 0,
        position: state.players.length + 1,
        color: pickNextPlayerColor(usedColors),
        hasFirstEntry: false,
        isWinner: false,
        isHost: false,
        statistics: { totalTurns: 0, totalPasses: 0, totalNegativePoints: 0, averageScore: 0, totalOvertakes: 0, totalPointsDelta: 0 },
        status: 'active',
        role: 'player',
      };
      newState = { ...state, players: [...state.players, newPlayer] };
      break;
    }
    case 'MARK_SELF_PLAYER': {
      newState = {
        ...state,
        players: state.players.map((player) => ({
          ...player,
          isSelf: action.playerId != null && player.id === action.playerId,
        })),
      };
      break;
    }
    case 'MERGE_EXTERNAL_PLAYERS': { newState = handleMergeExternalPlayers(state, action.players); break; }
    case 'REORDER_PLAYERS': {
      newState = { ...state, players: action.players.map((p, idx) => ({ ...p, position: idx + 1 })) };
      break; }
    case 'START_GAME': {
      const nextId = action.gameId && action.gameId.trim().length > 0
        ? action.gameId.trim().toUpperCase()
        : (state.id || generateGameId());
      const nextMode = action.mode ?? 'REAL';
      const groupId = nextMode === 'ONLINE' ? (action.groupId ?? state.groupId ?? null) : null;
      const groupToken = nextMode === 'ONLINE' ? (action.groupToken ?? state.groupToken ?? null) : null;
      const preservedEvents = nextMode === 'REAL'
        ? (state.events || []).filter(ev => ev.type === 'ADD_PLAYER')
        : [];
      newState = {
        ...state,
        id: nextId,
        mode: nextMode,
        groupId,
        groupToken,
        lobby: nextMode === 'ONLINE' ? { phase: 'LOBBY', readyMap: {}, nextGameParticipation: {} } : undefined,
        spectators: [],
        turnCycle: undefined,
        segments: [],
        events: preservedEvents,
        history: [],
        isGameOver: false,
        winner: undefined,
        postGameSummary: null,
        currentPlayerIndex: 0,
        turnNumber: 1,
        startedAt: state.startedAt || new Date().toISOString(),
        segmentsOwnerId: undefined,
        // Nowa gra = świeżo wylosowana mapa stref (albo jej brak, gdy opcja wyłączona).
        zones: action.zones,
      } as GameState;
      break;
    }
    case 'ADD_SCORE': {
      const { playerId, points } = action;
      if (points < SCORE_DELTA_MIN || points > SCORE_DELTA_MAX) return state;
      const events = state.events || [];
      const turnForAction = action.turn ?? state.turnNumber;
      const turnHasTurnEnd = events.some(ev => ev.type === 'TURN_END' && ev.turn === turnForAction);
      const turnEndScored = turnHasTurnEnd && events.some(
        (ev) => ev.type === 'SCORE' && ev.turn === turnForAction && ev.payload.playerId === playerId
      );
      if (turnEndScored) return state;
      const playerIndex = state.players.findIndex(p => p.id === playerId);
      if (playerIndex === -1 || state.isGameOver) return state;
      debug('ADD_SCORE:start', { playerId: playerId.slice(0, 8), points });

      const currentPlayer = state.players[playerIndex];
      const actionTimestamp = new Date();
      const lastRollInfo = state.turnCycle?.lastRoll;
      const reconstructedFirstRoll = !lastRollInfo ? deriveFirstRollInfoFromEvents(state, playerId) : null;

      if (!currentPlayer.hasFirstEntry && points > 0 && points < gameRules.scoring.firstEntryMin) return state;
      const canUseSingleDie = currentPlayer.score >= gameRules.scoring.minScoringDice.thresholdScore;
      const validation = validateAndComputeScore(points, currentPlayer.score, currentPlayer.hasFirstEntry, canUseSingleDie, state.settings.tunnels);
      if (!validation.isValid) {
        const notice = {
          id: `notice-${Date.now()}`,
          playerId,
          message: validation.error || 'Nieprawidłowy wynik (tunel/tora wyjścia).',
          kind: 'warning' as const,
          ts: Date.now(),
        };
        return pushNotice(state, notice);
      }

      const outcome = computeTurnOutcome({
        state,
        playerId,
        points,
        turnForAction,
        actionTimestamp,
        lastRollInfo,
        reconstructedFirstRoll,
      });

      if (!outcome || !outcome.historyEntry) return state;

      const {
        finalScore,
        nextHasFirstEntry,
        overtakesCount,
        newEvents,
        updatedPlayers,
        updatedZones,
        historyEntry,
      } = outcome;

      const isWinner = finalScore === gameRules.scoring.winScore;
      const statsContext = {
        previousScore: currentPlayer.score,
        newScore: finalScore,
        baseDelta: finalScore - currentPlayer.score,
        isPass: finalScore <= currentPlayer.score,
      };
      const updatedPlayer = applyTurnStats(currentPlayer, statsContext);
      if (overtakesCount > 0) {
        updatedPlayer.statistics = {
          ...updatedPlayer.statistics,
          totalOvertakes: (updatedPlayer.statistics.totalOvertakes || 0) + overtakesCount,
        };
      }
      updatedPlayers[playerIndex] = {
        ...updatedPlayer,
        score: finalScore,
        hasFirstEntry: nextHasFirstEntry,
        isWinner,
      };

      const finalPlayers = updatePlayerPositions(updatedPlayers);
      const nextPlayerIndex = isWinner ? playerIndex : (state.currentPlayerIndex + 1) % state.players.length;
      const nextTurnNumber = nextPlayerIndex === 0 ? state.turnNumber + 1 : state.turnNumber;
      const appendedHistory = [...state.history, historyEntry];
      const nextFirstEntries = nextHasFirstEntry ? { ...(state.firstEntries || {}), [playerId]: true } : state.firstEntries;
      newState = {
        ...state,
        players: finalPlayers,
        zones: updatedZones ?? state.zones,
        currentPlayerIndex: nextPlayerIndex,
        turnNumber: nextTurnNumber,
        isGameOver: isWinner,
        winner: isWinner ? finalPlayers[playerIndex] : undefined,
        history: appendedHistory.length > HISTORY_LIMIT ? appendedHistory.slice(-HISTORY_LIMIT) : appendedHistory,
        firstEntries: nextFirstEntries,
        events: [...(state.events || []), ...newEvents],
      };
      debug('ADD_SCORE:end', { turn: newState.turnNumber });
      break;
    }
    case 'UNDO_LAST_ACTION': { newState = handleUndoLastAction(state, debug); break; }
    case 'UNDO_TURN': { return handleUndoTurn(state, action.turn); }
    case 'RESET_GAME': {
      const targetMode = action.mode || 'REAL';
      newState = {
        ...initialState,
        mode: targetMode,
        segments: [],
        segmentsOwnerId: undefined,
        lobby: targetMode === 'ONLINE' ? { phase: 'LOBBY', readyMap: {}, nextGameParticipation: {} } : undefined,
        settings: { tunnels: true, rescueByFive: true, zones: false, zonesAvoidTunnels: false },
        zones: undefined,
      };
      break;
    }
    case 'SET_MODE': {
      if (state.mode === action.mode) return state;
      if (action.mode === 'REAL') {
        newState = { ...state, mode: 'REAL', lobby: undefined, turnCycle: undefined, postGameSummary: null };
      } else {
        newState = { ...state, mode: 'ONLINE', lobby: state.lobby || { phase: 'LOBBY', readyMap: {}, nextGameParticipation: {} }, postGameSummary: null };
      }
      break;
    }
    case 'SET_GROUP': {
      newState = { ...state, groupId: action.groupId ?? null, groupToken: action.groupToken ?? null };
      break;
    }
    case 'TOGGLE_TUNNELS': { newState = { ...state, settings: { ...state.settings, tunnels: !state.settings.tunnels } }; break; }
    case 'TOGGLE_RESCUE_BY_FIVE': { newState = { ...state, settings: { ...state.settings, rescueByFive: !state.settings.rescueByFive } }; break; }
    case 'TOGGLE_ZONES': { newState = { ...state, settings: { ...state.settings, zones: !state.settings.zones } }; break; }
    case 'TOGGLE_ZONES_AVOID_TUNNELS': { newState = { ...state, settings: { ...state.settings, zonesAvoidTunnels: !state.settings.zonesAvoidTunnels } }; break; }
    case 'UPDATE_SETTINGS': { newState = { ...state, settings: { ...state.settings, ...action.settings } }; break; }
    case 'LOAD_GAME': {
      const fe = action.gameState.firstEntries || {};
      const syncedPlayers = (action.gameState.players || []).map((p) => {
        const base = fe[p.id] ? { ...p, hasFirstEntry: true } : p;
        return withDefaultStatus(base as Player);
      });
      newState = {
        ...initialState,
        ...action.gameState,
        firstEntries: fe,
        players: assignPlayerColors(syncedPlayers),
        history: action.gameState.history || [],
        events: action.gameState.events || [],
        notices: action.gameState.notices || [],
        spectators: action.gameState.spectators || [],
      };
      break;
    }
    case 'SET_GAME_ID': {
      if (!action.id || state.id === action.id) return state;
      newState = { ...state, id: action.id };
      break;
    }
    case 'INIT_ONLINE_LOBBY': { newState = handleInitOnlineLobby(state, action); break; }
    case 'FINALIZE_ORDER': { newState = handleFinalizeOrder(state, action.order); break; }
    case 'GAME_STARTED': { newState = handleBeginOnlineGame(state, action.firstPlayerId); break; }
    case 'SET_LOBBY_PHASE': { newState = handleSetLobbyPhase(state, action.phase); break; }
    case 'ROLL_DICE': {
      if (state.mode === 'REAL' || !state.turnCycle?.activePlayerId) return state;
      if (state.turnCycle.activePlayerId !== action.playerId) return state;
      
      const isHotDiceReset = action.isHotDiceReset ?? false;
      debug('reducer:ROLL_DICE:before', {
        rollCount: state.turnCycle.rollCount,
        prevKept: state.turnCycle.kept,
        prevCaptured: state.turnCycle.capturedMask,
        newCaptured: state.turnCycle.capturedMask,
        isHotDiceReset,
        segmentsCount: (state.segments || []).length,
        incomingDice: action.dice,
        incomingKeptMask: action.keptMask,
      });
      
      const ev: GameEvent = {
        id: action.eventId,
        type: 'DICE_ROLL',
        turn: state.turnNumber,
        timestamp: new Date().toISOString(),
        payload: {
          playerId: action.playerId,
          rollIndex: action.rollIndex,
          dice: action.dice,
          keptMask: action.keptMask,
          isHotDiceReset: action.isHotDiceReset ?? false,
          eventId: action.eventId,
        },
      };
      const rollResult = computeRollState({
        state,
        dice: action.dice,
        keptMask: action.keptMask,
        rollIndex: action.rollIndex,
        eventId: action.eventId,
        isHotDiceReset,
        rolledDice: action.rolledDice,
        rolledIndices: action.rolledIndices,
        rollMeta: action.rollMeta,
      });

      debug('reducer:ROLL_DICE:after', {
        nextDice: action.dice,
        nextKept: action.keptMask,
        nextCaptured: rollResult.capturedMask,
        rolledDice: rollResult.derivedDice,
        rolledIndices: rollResult.derivedIndices,
        meta: rollResult.meta,
      });
      
      newState = {
        ...state,
        segments: rollResult.segments,
        segmentsOwnerId: state.turnCycle?.activePlayerId || state.segmentsOwnerId,
        turnCycle: {
          ...state.turnCycle,
          dice: action.dice,
          rollCount: state.turnCycle.rollCount + 1,
          status: rollResult.status,
          kept: action.keptMask,
          capturedMask: rollResult.capturedMask,
          lastRoll: rollResult.lastRoll,
          diceOrigins: rollResult.diceOrigins,
        },
        events: [...(state.events || []), ev],
      };
      break; }
    case 'TOGGLE_KEEP': {
      const result = computeToggleKeepResult(state, action.index);
      if (!result.ok || !state.turnCycle) {
        newState = state;
        break;
      }
      debug?.('reducer:TOGGLE_KEEP', {
        index: action.index,
        diceValue: state.turnCycle.dice?.[action.index],
        prevKept: result.prevKept,
        newKept: result.nextKept,
        keptCount: result.nextKept.filter(Boolean).length,
        capturedMask: result.capturedMask,
        enforceGroup: result.enforceGroup,
      });
      newState = {
        ...state,
        turnCycle: {
          ...state.turnCycle,
          kept: result.nextKept,
          status: result.status,
        },
        events: [...(state.events || []), result.event],
      };
      break;
    }
    case 'END_TURN_INTERNAL': {
      newState = handleEndTurnInternal(state, action.playerId);
      break;
    }
    case 'MARK_TURN_FINALIZING': {
      newState = handleMarkTurnFinalizing(state);
      break;
    }
    case 'APPLY_DICE_ROLL': {
      if (state.mode === 'REAL' || !state.turnCycle) return state;
      if (state.turnCycle.activePlayerId !== action.playerId) return state;
      const rollResult = computeRollState({
        state,
        dice: action.dice,
        keptMask: action.keptMask,
        rollIndex: action.rollIndex,
        eventId: action.eventId,
      });
      const status: typeof state.turnCycle.status = rollResult.status;
      newState = {
        ...state,
        segments: rollResult.segments,
        segmentsOwnerId: state.turnCycle?.activePlayerId || state.segmentsOwnerId,
        turnCycle: {
          ...state.turnCycle,
          dice: action.dice,
          rollCount: action.rollIndex + 1,
          status,
          kept: action.keptMask,
          capturedMask: rollResult.capturedMask,
          lastRoll: rollResult.lastRoll,
          diceOrigins: rollResult.diceOrigins,
        },
      };
      break; }
    case 'APPLY_DICE_KEEP': {
      newState = handleApplyDiceKeep(state, action.playerId, action.rollIndex, action.keptMask);
      break;
    }
    case 'TOGGLE_READY': { newState = handleToggleReady(state, action.playerId, action.ready); break; }
    case 'SET_HISTORY': { newState = { ...state, history: action.history }; break; }
    case 'SET_EVENTS': { newState = { ...state, events: action.events }; break; }
    case 'ADD_EVENT': { newState = { ...state, events: [...(state.events || []), action.event] }; break; }
    case 'ROLLBACK_EVENT': {
      const filtered = (state.events || []).filter((event) => event.id !== action.eventId);
      if (filtered.length === (state.events || []).length) {
        newState = state;
        break;
      }
      let rebuilt = baseStateForReplay(state);
      for (const event of filtered) {
        rebuilt = handleApplyEvent({ state: rebuilt, event });
      }
      newState = rebuilt;
      break;
    }
    case 'TRIM_EVENTS': { newState = handleTrimEvents(state, action.events); break; }
    case 'HYDRATE_FROM_SNAPSHOT': { newState = handleHydrateFromSnapshot(state, action.snapshot); break; }
    case 'HYDRATE_LOBBY_STATE': { newState = handleHydrateLobbyState(state, action.snapshot); break; }
    case 'APPLY_EVENT': { newState = handleApplyEvent({ state, event: action.event }); break; }
    default: newState = state; break;
  }
  return ensureOnlineTurnCycle(newState);
}
